import { useEffect, useState, useCallback } from "react";
import { Search, Plus, Minus, Coins, X, RefreshCw } from "lucide-react";
import { StripeCheckModal } from "./StripeCheckModal";
import { apiAdminMembers, apiAdminAdjustCredits, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function MembersTab() {
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [stripeTarget, setStripeTarget] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiAdminMembers({ search: search.trim() || undefined, status: statusFilter || undefined });
      setMembers(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="mt-5" data-testid="members-tab">
      <div className="flex flex-wrap items-center gap-3" data-testid="members-filters">
        <div className="flex items-center gap-2 border border-neutral-800 bg-neutral-950 px-3 py-2 flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 text-neutral-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search email or name…"
            data-testid="members-search"
            className="flex-1 bg-transparent text-sm text-white placeholder-neutral-600 outline-none"
          />
        </div>
        <div className="flex items-center gap-1">
          {["", "active", "inactive"].map((s) => (
            <button
              key={s || "all"}
              onClick={() => setStatusFilter(s)}
              data-testid={`members-filter-${s || "all"}`}
              className={`font-mono text-[10px] uppercase tracking-[0.2em] px-2.5 py-1.5 border ${
                statusFilter === s ? "border-white text-white" : "border-neutral-800 text-neutral-400 hover:text-white hover:border-neutral-600"
              }`}
            >{s || "all"}</button>
          ))}
        </div>
      </div>

      <div className="mt-4 border border-neutral-900 bg-neutral-950 overflow-x-auto" data-testid="members-list">
        <table className="w-full text-sm" >
          <thead className="border-b border-neutral-900">
            <tr className="text-left text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500">
              <th className="px-4 py-3">name / email</th>
              <th className="px-3 py-3">member</th>
              <th className="px-3 py-3 text-right">credits</th>
              <th className="px-3 py-3">subscription</th>
              <th className="px-3 py-3 text-right">actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-6 text-xs font-mono uppercase tracking-[0.25em] text-neutral-600">loading…</td></tr>
            )}
            {!loading && members.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-6 text-xs font-mono uppercase tracking-[0.25em] text-neutral-600">no members</td></tr>
            )}
            {members.map((m) => (
              <tr key={m.user_id} className="border-b border-neutral-900 hover:bg-neutral-900/40" data-testid={`member-row-${m.user_id}`}>
                <td className="px-4 py-3">
                  <div className="text-white font-medium">{m.name || "—"}</div>
                  <div className="text-xs text-neutral-500">{m.email}</div>
                </td>
                <td className="px-3 py-3">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em]"
                        style={{ color: m.active ? "#00FF66" : "#888" }}>
                    {m.active ? "active" : "inactive"}
                  </span>
                </td>
                <td className="px-3 py-3 text-right">
                  <div className="font-mono text-white">{m.credits}</div>
                  <div className="text-[10px] font-mono text-neutral-600">
                    s:{m.credits_subscription} · p:{m.credits_purchased} · a:{m.credits_admin_grant}
                  </div>
                </td>
                <td className="px-3 py-3 text-xs text-neutral-400 font-mono uppercase tracking-[0.15em]">
                  {m.subscription_status || "—"}
                </td>
                <td className="px-3 py-3 text-right whitespace-nowrap">
                  <button
                    onClick={() => setStripeTarget(m)}
                    data-testid={`stripe-check-${m.user_id}`}
                    className="inline-flex items-center gap-1 mr-1.5 border border-neutral-800 hover:border-[#00E5FF] hover:text-[#00E5FF] text-neutral-300 font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1.5 transition-colors"
                  >
                    <RefreshCw className="w-3 h-3" /> stripe
                  </button>
                  <button
                    onClick={() => setAdjustTarget(m)}
                    data-testid={`adjust-credits-${m.user_id}`}
                    className="inline-flex items-center gap-1 border border-neutral-800 hover:border-[#00E5FF] hover:text-[#00E5FF] text-neutral-300 font-mono text-[10px] uppercase tracking-[0.2em] px-2 py-1.5 transition-colors"
                  >
                    <Coins className="w-3 h-3" /> adjust
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {stripeTarget && (
        <StripeCheckModal member={stripeTarget} onClose={() => setStripeTarget(null)} onApplied={load} />
      )}

      {adjustTarget && (
        <AdjustCreditsModal
          member={adjustTarget}
          onClose={() => setAdjustTarget(null)}
          onSaved={() => { setAdjustTarget(null); load(); }}
        />
      )}
    </div>
  );
}

function AdjustCreditsModal({ member, onClose, onSaved }) {
  const [delta, setDelta] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const apply = async (sign) => {
    const n = Math.abs(parseInt(delta || "0", 10));
    if (!n || isNaN(n)) { toast.error("Enter a number > 0"); return; }
    if (!reason.trim()) { toast.error("Reason required"); return; }
    setBusy(true);
    try {
      const result = await apiAdminAdjustCredits(member.user_id, sign * n, reason.trim());
      toast.success(`Balance ${sign > 0 ? "+" : "-"}${n} → ${result.balance}`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="adjust-credits-modal"
    >
      <div
        className="bg-neutral-950 border border-neutral-800 w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-900">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-neutral-500">adjust credits</div>
            <div className="font-bold text-white mt-0.5">{member.email}</div>
            <div className="text-[10px] font-mono text-neutral-600 mt-0.5">current balance: {member.credits}</div>
          </div>
          <button onClick={onClose} className="text-neutral-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1.5">amount</div>
            <input
              type="number" min="1" value={delta}
              onChange={(e) => setDelta(e.target.value)}
              placeholder="e.g. 50"
              data-testid="adjust-amount"
              className="w-full bg-black border border-neutral-800 px-3 py-2 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-[#00E5FF]"
            />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1.5">reason (required)</div>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              placeholder="e.g. compensation, promotional grant…"
              data-testid="adjust-reason"
              className="w-full bg-black border border-neutral-800 px-3 py-2 text-sm text-white placeholder-neutral-600 focus:outline-none focus:border-[#00E5FF] resize-none"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <button
              onClick={() => apply(-1)} disabled={busy}
              data-testid="adjust-remove"
              className="flex-1 inline-flex items-center justify-center gap-1.5 border border-red-900 hover:bg-red-950/40 text-red-400 font-mono text-xs uppercase tracking-[0.2em] px-3 py-2.5 transition-colors disabled:opacity-50"
            >
              <Minus className="w-3.5 h-3.5" /> Remove
            </button>
            <button
              onClick={() => apply(+1)} disabled={busy}
              data-testid="adjust-add"
              className="flex-1 inline-flex items-center justify-center gap-1.5 bg-[#00FF66] hover:bg-[#00DD55] text-black font-bold text-xs uppercase tracking-[0.15em] px-3 py-2.5 transition-colors disabled:opacity-50"
            >
              <Plus className="w-3.5 h-3.5" /> Add
            </button>
          </div>
          <div className="text-[10px] font-mono text-neutral-600 leading-relaxed pt-2">
            Added credits are admin grants and never expire. Removed credits consume FIFO from existing lots.
          </div>
        </div>
      </div>
    </div>
  );
}
